/**
 * Query Keys Factory
 * Provides type-safe, hierarchical query keys following TanStack Query best practices
 *
 * Use this for async operations like API calls, not for synchronous Office.js operations
 */

export const queryKeys = {
  // Root-level keys
  all: ['outlook'] as const,

  // Authentication queries
  auth: {
    all: ['outlook', 'auth'] as const,
    deployments: () => ['outlook', 'auth', 'deployments'] as const,
    login: () => ['outlook', 'auth', 'login'] as const,
    msalToken: () => ['outlook', 'auth', 'msal-token'] as const,
    session: () => ['outlook', 'auth', 'session'] as const,
    user: () => ['outlook', 'auth', 'user'] as const,
  },

  chat: {
    all: ['outlook', 'chat'] as const,
    folders: () => ['outlook', 'chat', 'folders'] as const,
    sendMessage: (sessionId: string) => ['outlook', 'chat', 'sendMessage', sessionId] as const,
    session: (sessionId: string) => ['outlook', 'chat', 'session', sessionId] as const,
    sessions: (search?: string) => ['outlook', 'chat', 'sessions', search] as const,
    suggestions: (scope: string, sessionId?: string, messageId?: string, conversationId?: string) =>
      ['outlook', 'chat', 'suggestions', scope, sessionId, messageId, conversationId] as const,
  },

  inbox: {
    all: ['outlook', 'inbox'] as const,
    contextEmails: (contextEmailIds?: Array<string>) =>
      ['outlook', 'inbox', 'contextEmails', ...(contextEmailIds ?? [])] as const,
    fetch: (
      limit?: number,
      search?: string,
      contextEmailIds?: Array<string>,
      sessionId?: string | null | undefined,
    ) =>
      ['outlook', 'inbox', 'fetch', limit, search, sessionId, ...(contextEmailIds ?? [])] as const,
  },

  user: {
    all: ['outlook', 'user'] as const,
    assistantPreferences: () => ['outlook', 'user', 'assistant-preferences'] as const,
    initialSyncStatus: () => ['outlook', 'user', 'initial-sync-status'] as const,
    intercomToken: () => ['outlook', 'user', 'intercom-token'] as const,
  },

  // API-related queries (example structure for future async operations)
  api: {
    all: ['outlook', 'api'] as const,
  },
} as const;
